import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import yardMapUrl from '../../assets/yard/fe-site-yard-app.svg';
import yardZones from '../../assets/yard/fe-site-yard-zones.json';
import { YardZoneGuidance, yardZoneLabel } from '../../utils/yardZoneGuidance';

interface YardZoneRecord {
  zone_code: string;
  name: string;
  polygon_points: number[][];
  centroid_svg: number[];
  protection_class?: string;
}

interface YardZonesFile {
  yard_map: {
    view_box: string;
    effective_from?: string;
  };
  zones: YardZoneRecord[];
}

interface YardRouteMapProps {
  guidance: YardZoneGuidance | null;
  materialName?: string | null;
  compact?: boolean;
}

const typedYardZones = yardZones as unknown as YardZonesFile;

const polygonPoints = (zone: YardZoneRecord) =>
  zone.polygon_points
    .filter((point) => point.length >= 2)
    .map(([x, y]) => `${x},${y}`)
    .join(' ');

const zoneCentroid = (zone: YardZoneRecord): [number, number] | null =>
  zone.centroid_svg && zone.centroid_svg.length >= 2
    ? [zone.centroid_svg[0], zone.centroid_svg[1]]
    : null;

const labelLines = (zone: YardZoneRecord): string[] => {
  const words = zone.name.split(/\s+/).filter(Boolean);
  const lines = [zone.zone_code];
  let current = '';
  words.forEach((word) => {
    const next = current ? `${current} ${word}` : word;
    if (next.length > 16 && current) {
      lines.push(current);
      current = word;
    } else {
      current = next;
    }
  });
  if (current) lines.push(current);
  return lines;
};

const labelBoxWidth = (lines: string[]) =>
  Math.max(34, Math.max(...lines.map((line) => line.length)) * 5.4 + 12);

const isStorageDestination = (zone: YardZoneRecord) => /^Z\d+/.test(zone.zone_code);

export default function YardRouteMap({ guidance, materialName, compact = false }: YardRouteMapProps) {
  const zone = typedYardZones.zones.find((row) => row.zone_code === guidance?.zoneCode) ?? null;
  const receivalZone = typedYardZones.zones.find((row) => row.zone_code === 'A1a') ?? null;
  const routeStart: [number, number] = receivalZone ? zoneCentroid(receivalZone) ?? [86, 430] : [86, 430];
  const destination = zone ? zoneCentroid(zone) : null;
  const routePath = destination
    ? `M ${routeStart[0]} ${routeStart[1]} C ${routeStart[0] + 120} ${routeStart[1] - 65}, ${destination[0] - 115} ${destination[1] + 55}, ${destination[0]} ${destination[1]}`
    : '';

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.75 }}>
        <Chip size="small" color={zone ? 'primary' : 'default'} label={guidance ? yardZoneLabel(guidance) : 'No yard route'} />
        {materialName && (
          <Typography variant="body2" fontWeight={800}>
            {materialName}
          </Typography>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
        Truck drop-off starts at A1a - Receival area, then routes to the assigned storage location.
      </Typography>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '739.19 / 490.39',
          minHeight: compact ? 220 : 300,
          bgcolor: '#f7f8f5',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          '@keyframes yardRoutePulse': {
            '0%': { opacity: 0.35, transform: 'scale(0.95)' },
            '50%': { opacity: 1, transform: 'scale(1.08)' },
            '100%': { opacity: 0.35, transform: 'scale(0.95)' },
          },
        }}
      >
        <Box
          component="img"
          src={yardMapUrl}
          alt="Fairmont Phase 1B site yard drawing"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
        />
        <Box
          component="svg"
          viewBox={typedYardZones.yard_map.view_box}
          preserveAspectRatio="xMidYMid meet"
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
        >
          {zone && (
            <>
              <polygon
                points={polygonPoints(zone)}
                fill="rgba(25, 118, 210, 0.28)"
                stroke="#0d47a1"
                strokeWidth="3"
              />
              <path d={routePath} fill="none" stroke="#d32f2f" strokeWidth="4" strokeDasharray="10 8" strokeLinecap="round" />
              <circle cx={routeStart[0]} cy={routeStart[1]} r="7" fill="#d32f2f" />
              <text x={routeStart[0] + 12} y={routeStart[1] + 4} fontSize="14" fontWeight="700" fill="#8a1c1c">
                A1a - Receival area
              </text>
              <circle cx={destination![0]} cy={destination![1]} r="14" fill="#1976d2" opacity="0.9" />
              <circle
                cx={destination![0]}
                cy={destination![1]}
                r="25"
                fill="none"
                stroke="#1976d2"
                strokeWidth="4"
                style={{ animation: 'yardRoutePulse 1.4s ease-in-out infinite', transformOrigin: `${destination![0]}px ${destination![1]}px` }}
              />
              <text x={destination![0] + 18} y={destination![1] + 5} fontSize="18" fontWeight="900" fill="#0d47a1">
                {zone.zone_code}
              </text>
              <circle r="6" fill="#ffeb3b" stroke="#d32f2f" strokeWidth="2">
                <animateMotion dur="2.4s" repeatCount="indefinite" path={routePath} />
              </circle>
            </>
          )}
          {typedYardZones.zones.map((labelZone) => {
            const centroid = zoneCentroid(labelZone);
            if (!centroid) return null;
            const selected = labelZone.zone_code === zone?.zone_code;
            const routeOrigin = labelZone.zone_code === 'A1a';
            const lines = selected || routeOrigin ? labelLines(labelZone) : [labelZone.zone_code];
            const width = labelBoxWidth(lines);
            const lineHeight = selected || routeOrigin ? 10 : 8;
            const height = lines.length * lineHeight + 8;
            return (
              <g key={labelZone.zone_code}>
                <rect
                  x={centroid[0] - width / 2}
                  y={centroid[1] - height / 2}
                  width={width}
                  height={height}
                  rx="3"
                  fill={selected ? '#0d47a1' : routeOrigin ? '#8a1c1c' : 'rgba(255, 255, 255, 0.84)'}
                  stroke={selected || routeOrigin ? '#ffffff' : 'rgba(0, 0, 0, 0.5)'}
                  strokeWidth={selected || routeOrigin ? 2 : 0.7}
                />
                <text
                  x={centroid[0]}
                  y={centroid[1] - ((lines.length - 1) * lineHeight) / 2 + (selected || routeOrigin ? 3 : 2)}
                  textAnchor="middle"
                  fontSize={selected || routeOrigin ? 9 : 8}
                  fontWeight="900"
                  fill={selected || routeOrigin ? '#ffffff' : '#111827'}
                >
                  {lines.map((line, index) => (
                    <tspan key={`${labelZone.zone_code}-${line}-${index}`} x={centroid[0]} dy={index === 0 ? 0 : lineHeight}>
                      {line}
                    </tspan>
                  ))}
                </text>
              </g>
            );
          })}
        </Box>
      </Box>
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontWeight: 900, textTransform: 'uppercase' }}>
          Storage location labels
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ overflowX: 'auto', pb: 0.5 }}>
          {typedYardZones.zones.filter(isStorageDestination).map((labelZone) => (
            <Chip
              key={labelZone.zone_code}
              size="small"
              variant={labelZone.zone_code === zone?.zone_code ? 'filled' : 'outlined'}
              color={labelZone.zone_code === zone?.zone_code ? 'primary' : 'default'}
              label={`${labelZone.zone_code} - ${labelZone.name}`}
            />
          ))}
          {zone && !isStorageDestination(zone) && (
            <Chip size="small" color="primary" label={`${zone.zone_code} - ${zone.name}`} />
          )}
        </Stack>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        {zone
          ? `Destination: ${zone.zone_code} - ${zone.name}${zone.protection_class ? `, protection class ${zone.protection_class}` : ''}.`
          : 'No mapped destination is available for this material yet.'}
      </Typography>
    </Box>
  );
}
